import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import DashboardHeaderV2 from './DashboardHeaderV2';
import Sidebar from './Sidebar';
import { pageLabelForPath, setDashboardTitle, restoreBaseTitle } from '../pageTitles';
import { QuizProvider } from '../context/QuizContext';
import { API_BASE_URL } from '../../../config/api';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { NotificationsProvider } from '../../../contexts/NotificationsContext';
import SubscriptionBanner from '../../../components/SubscriptionBanner';

// Channel name for coordinating dashboard tabs
const DASHBOARD_CHANNEL = 'nuvola-dashboard-tab';

const DashboardLayout = () => {
  const [sessionExpired, setSessionExpired] = useState(false);
  const { refetch } = useSubscription();
  const location = useLocation();

  // Browser-tab title per page ("No / SCAM - Fraud Alerts"), resolved from the
  // route; detail pages refine it once their data loads. Restored to the
  // site's base title when the dashboard unmounts.
  useEffect(() => {
    setDashboardTitle(pageLabelForPath(location.pathname));
  }, [location.pathname]);
  useEffect(() => restoreBaseTitle, []);

  // Scales the whole dashboard below xl — see .dashboard-scale in index.css.
  // On <html> because rem resolves against the root, not an ancestor; removed
  // on unmount so the marketing site keeps its own sizing.
  useEffect(() => {
    document.documentElement.classList.add('dashboard-scale');
    return () => document.documentElement.classList.remove('dashboard-scale');
  }, []);

  // Listen for SESSION_REFRESH broadcasts from other tabs
  useEffect(() => {
    let channel;
    try {
      channel = new BroadcastChannel(DASHBOARD_CHANNEL);

      channel.onmessage = (event) => {
        if (event.data?.type === 'SESSION_REFRESH') {
          // New transfer completed in another tab — reload to pick up the fresh session
          window.location.reload();
        }
      };
    } catch (e) {
      console.log('[Dashboard] BroadcastChannel not supported');
    }

    return () => {
      if (channel) {
        channel.close();
      }
    };
  }, []);

  // After a DTA transfer completes (redirect from backend), notify OTHER tabs
  // The backend appends ?transferred=true to the redirect URL after creating the session
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('transferred') !== 'true') return;

    // Clean the URL immediately so refresh doesn't re-broadcast
    params.delete('transferred');
    const cleanUrl = window.location.pathname + (params.toString() ? `?${params}` : '');
    window.history.replaceState({}, '', cleanUrl);

    // Broadcast to other tabs — session exists at this point
    try {
      const channel = new BroadcastChannel(DASHBOARD_CHANNEL);
      channel.postMessage({ type: 'SESSION_REFRESH' });
      // Delay close to ensure message delivery across tabs
      setTimeout(() => channel.close(), 300);
    } catch (e) {
      // BroadcastChannel not supported — visibility polling fallback handles this
    }
  }, []);

  // Fallback: check session on tab focus (handles BroadcastChannel unsupported/unreliable)
  useEffect(() => {
    let lastKnownUser = null;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      try {
        const res = await fetch(`${API_BASE_URL}/auth/status`, { credentials: 'include' });
        const data = await res.json();

        if (lastKnownUser === null) {
          // First check — just record current state
          lastKnownUser = data.authenticated ? data.user?.id : null;
          return;
        }

        const currentUser = data.authenticated ? data.user?.id : null;
        if (currentUser !== lastKnownUser) {
          // Session changed (different user or logged out) — reload
          window.location.reload();
        }
      } catch (e) {
        // Network error — don't reload, just ignore
      }
    };

    // Set initial user on mount
    handleVisibilityChange();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Session heartbeat — detect server-side session expiry
  useEffect(() => {
    const HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 minutes

    const checkHeartbeat = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/bff/heartbeat`, { credentials: 'include' });
        const data = await res.json();
        if (!data.alive) {
          setSessionExpired(true);
          // Invalidate subscription context so ProtectedRoute kicks the user to login
          // on the next navigation (otherwise stale cached authenticated=true lets them browse)
          refetch();
        }
      } catch (e) {
        // Network error — don't show banner (might be offline)
      }
    };

    const interval = setInterval(checkHeartbeat, HEARTBEAT_INTERVAL);
    return () => clearInterval(interval);
  }, [refetch]);

  return (
    <QuizProvider>
      <NotificationsProvider>
      <div className="flex flex-col h-screen bg-gray-50">
        {/* Session expired banner */}
        {sessionExpired && (
          <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <span className="text-yellow-800 text-sm">
              Your session has expired. Please sign in again to continue.
            </span>
            <button
              onClick={() => {
                const returnUrl = encodeURIComponent(window.location.pathname);
                window.location.href = `${API_BASE_URL}/auth/login?returnUrl=${returnUrl}`;
              }}
              className="bg-yellow-600 text-white px-4 py-1.5 rounded text-sm font-medium hover:bg-yellow-700 ml-4 flex-shrink-0"
            >
              Sign in
            </button>
          </div>
        )}

        {/* Subscription status banner (past_due, cancel_at_period_end, canceled-in-grace) */}
        <SubscriptionBanner />

        {/* Minimal Header */}
        <DashboardHeaderV2 />

        {/* Sidebar + Content */}
        <div className="flex flex-1 overflow-hidden">
          <Sidebar />
          <main className="flex-1 overflow-hidden">
            <Outlet />
          </main>
        </div>
      </div>
      </NotificationsProvider>
    </QuizProvider>
  );
};

export default DashboardLayout;
